// Shared utilities for driver-facing schedule surfaces.
// Used by both:
//   - public/src/views/jobboard.js — secondary driver schedule surface
//   - public/src/views/dispatch.js — primary driver schedule surface
//     (the page driver-PIN flow lands on, via app.js:7868)
//
// These were previously duplicated/divergent. Extracting here so that
// load-counter behavior, personalized-schedule filtering, and legacy-safe
// driver-assignment matching are computed identically across both views.

// ─── Personalized Schedule (S&F-pattern subcontracting) ────────────────────

// True when the tenant has the "Personalized Schedule" setting on AND the
// caller is a driver-mode session. Doesn't apply to dispatcher views.
export function isPersonalizedScheduleActive(opts) {
  const { settings, driverPersonId } = opts || {};
  if (!settings?.personalizedSchedule) return false;
  return !!driverPersonId;
}

// Legacy-safe driver-assignment matcher. The Roster supports both Firestore
// IDs and old name strings on jobDay.drivers / jobDay.helpers / jobDay.people.
// This mirrors dispatch.js's existing per-driver filter (which the simpler
// "drivers[]-ID-only" matcher in the original jobboard filter missed).
export function isJobDayAssignedToDriver(jobDay, driverPersonId, driverPerson) {
  if (!jobDay || !driverPersonId) return false;
  const lists = [
    jobDay.drivers || [],
    jobDay.helpers || [],
    jobDay.people  || [],
  ];
  for (const list of lists) {
    for (const ref of list) {
      if (String(ref) === String(driverPersonId)) return true;
      if (driverPerson && (ref === driverPerson.name || ref === driverPerson.displayName)) return true;
    }
  }
  return false;
}

// Convenience: should this jobDay render for the current driver-mode session?
// Encapsulates the personalized-schedule precedence (tenant setting forces
// personalization across all drivers; otherwise per-person showAllJobs toggle
// controls; default is "show all" for backward compat).
export function jobDayVisibleToCurrentDriver(jobDay, opts) {
  const { settings, driverPersonId, driverPerson } = opts || {};
  // Not a driver session → all visible
  if (!driverPersonId) return true;
  // Tenant-level personalize → restrict to assigned only
  if (settings?.personalizedSchedule) {
    return isJobDayAssignedToDriver(jobDay, driverPersonId, driverPerson);
  }
  // Per-person opt-out preserved
  if (driverPerson && driverPerson.showAllJobs === false) {
    return isJobDayAssignedToDriver(jobDay, driverPersonId, driverPerson);
  }
  return true;
}

// ─── Load Calculator ───────────────────────────────────────────────────────

// Pure compute: given a jobDay + settings + asset/truck arrays, return
// { totalLoads, completed, remaining, pct, source } when:
//   - tenant has enableLoadCalculator on, AND EITHER
//   - (a) attached assets have unitWeight + attached trucks have payloadCapacity (source='weights'), OR
//   - (b) jobDay.loadsExpected is a positive number (source='manual') —
//         dispatcher-supplied target so drivers see "X / N · N-X remaining"
//         even on jobs without weights configured.
// Returns null otherwise (caller decides whether to render manual-only fallback).
export function computeLoadInfo(jobDay, settings, assets, trucks) {
  if (!settings?.enableLoadCalculator || !jobDay) return null;

  const completed = jobDay.loadsCompleted || 0;

  // Path A: weight-derived total
  const rawAssets = (jobDay.assets || []).map(id => {
    const a = (assets || []).find(x => x.id === id);
    if (!a || !a.unitWeight) return null;
    const qty = jobDay.assetQtys?.[id] || 1;
    return { totalWeight: a.unitWeight * qty };
  }).filter(Boolean);

  const totalWeight = rawAssets.reduce((s, a) => s + a.totalWeight, 0);
  const rawTrucks = (jobDay.trucks || []).map(id => {
    const t = (trucks || []).find(x => x.id === id);
    if (!t || !t.payloadCapacity) return null;
    return { capacity: t.payloadCapacity };
  }).filter(Boolean);

  if (totalWeight > 0 && rawTrucks.length > 0) {
    const totalCapacity = rawTrucks[0].capacity;
    const totalLoads = Math.ceil(totalWeight / totalCapacity);
    const remaining = Math.max(0, totalLoads - completed);
    const pct = totalLoads > 0 ? Math.round((completed / totalLoads) * 100) : 0;
    return { totalLoads, completed, remaining, pct, source: 'weights' };
  }

  // Path B: dispatcher-supplied manual target
  const manual = Number(jobDay.loadsExpected);
  if (Number.isFinite(manual) && manual > 0) {
    const totalLoads = Math.floor(manual);
    const remaining = Math.max(0, totalLoads - completed);
    const pct = totalLoads > 0 ? Math.round((completed / totalLoads) * 100) : 0;
    return { totalLoads, completed, remaining, pct, source: 'manual' };
  }

  return null;
}

// Render the load counter HTML for a jobDay. Three paths:
//   - rich (weights):  computeLoadInfo returns source='weights' — full progress bar
//   - rich (manual):   computeLoadInfo returns source='manual'  — same UI, dispatcher set the target
//   - fallback:        info is null BUT the jobDay has assets or trucks attached;
//                      drivers still want to log loads as they go even without weight data.
//
// opts:
//   recordModule: 'JobBoardModule' | 'DispatchModule' — onclick target
//   onclickPrefix: optional extra prefix (default 'event.stopPropagation();')
//   showDispatcherEdit: when true, render a small "Set expected total" link
//     under the counter (use only on dispatcher surfaces; drivers shouldn't
//     edit the target). The link calls
//     window.{recordModule}.promptSetLoadsExpected(jobDayId).
export function buildDriverLoadCounterHTML(jobDay, settings, assets, trucks, opts = {}) {
  // Master toggle off OR no jobDay → render nothing.
  if (!settings?.enableLoadCalculator || !jobDay) return '';

  const recordMod = opts.recordModule || 'JobBoardModule';
  const stopProp  = opts.onclickPrefix || 'event.stopPropagation();';

  const info = computeLoadInfo(jobDay, settings, assets, trucks);

  // Optional dispatcher-only "Set expected" link (rendered under any path).
  const editLink = opts.showDispatcherEdit
    ? `<button type="button" onclick="${stopProp} window.${recordMod}.promptSetLoadsExpected('${jobDay.id}')" style="background:transparent;border:0;color:#3B82F6;font-size:0.78rem;font-weight:600;padding:6px 0 0;cursor:pointer;text-decoration:underline;">${jobDay.loadsExpected ? 'Edit expected total' : 'Set expected total'}</button>`
    : '';

  if (info) {
    const { totalLoads, completed, remaining, pct, source } = info;
    const color = pct >= 100 ? '#10B981' : pct >= 50 ? '#F59E0B' : '#3B82F6';
    const sourceTag = source === 'manual'
      ? '<span style="font-size:0.7rem;color:var(--text-muted);font-weight:500;margin-left:6px;">(manual target)</span>'
      : '';
    return `
      <div style="background:rgba(59,130,246,0.05);border-radius:10px;padding:14px 16px;margin:12px 0;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
          <span class="material-icons" style="color:${color};font-size:22px;">local_shipping</span>
          <span style="font-weight:600;font-size:0.95rem;">Load Progress</span>${sourceTag}
        </div>
        <div style="display:flex;align-items:baseline;gap:8px;margin-bottom:8px;">
          <span style="font-size:1.6rem;font-weight:700;color:${color};">${completed}</span>
          <span style="font-size:0.9rem;color:#64748B;">/ ${totalLoads} loads</span>
          <span style="font-size:0.8rem;font-weight:600;color:${color};margin-left:auto;">${pct}%</span>
        </div>
        <div style="background:#e2e8f0;border-radius:4px;height:10px;overflow:hidden;">
          <div style="background:${color};height:100%;width:${Math.min(pct, 100)}%;border-radius:4px;transition:width 0.3s ease;"></div>
        </div>
        <div style="font-size:0.8rem;color:#64748B;margin-top:6px;">${remaining} load${remaining !== 1 ? 's' : ''} remaining</div>
        ${pct < 100 ? `<button class="btn btn-sm btn-primary" style="margin-top:10px;width:100%;padding:10px;font-size:0.95rem;" onclick="${stopProp} window.${recordMod}.recordLoadPickup('${jobDay.id}', ${completed}, ${totalLoads})">
          <span class="material-icons" style="font-size:18px;">add_circle</span> Load Picked Up
        </button>` : '<div style="text-align:center;margin-top:8px;font-weight:600;color:#10B981;">All loads complete!</div>'}
        ${completed > 0 ? `<button class="btn btn-sm btn-secondary" style="margin-top:6px;width:100%;font-size:0.8rem;" onclick="${stopProp} window.${recordMod}.undoLoadPickup('${jobDay.id}', ${completed})">
          <span class="material-icons" style="font-size:14px;">undo</span> Undo Last
        </button>` : ''}
        ${editLink}
      </div>`;
  }

  // Fallback — manual +/- counter, only when jobDay has assets or trucks.
  const hasAssetsOrTrucks = (jobDay.assets || []).length > 0 || (jobDay.trucks || []).length > 0;
  if (!hasAssetsOrTrucks) return '';
  const completed = jobDay.loadsCompleted || 0;
  return `
    <div style="background:rgba(59,130,246,0.05);border-radius:10px;padding:14px 16px;margin:12px 0;">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
        <span class="material-icons" style="color:#3B82F6;font-size:22px;">local_shipping</span>
        <span style="font-weight:600;font-size:0.95rem;">Loads</span>
      </div>
      <div style="display:flex;align-items:baseline;gap:8px;margin-bottom:6px;">
        <span style="font-size:1.6rem;font-weight:700;color:#3B82F6;">${completed}</span>
        <span style="font-size:0.9rem;color:#64748B;">completed</span>
      </div>
      <button class="btn btn-sm btn-primary" style="margin-top:10px;width:100%;padding:10px;font-size:0.95rem;" onclick="${stopProp} window.${recordMod}.recordLoadPickup('${jobDay.id}', ${completed}, 0)">
        <span class="material-icons" style="font-size:18px;">add_circle</span> Load Picked Up
      </button>
      ${completed > 0 ? `<button class="btn btn-sm btn-secondary" style="margin-top:6px;width:100%;font-size:0.8rem;" onclick="${stopProp} window.${recordMod}.undoLoadPickup('${jobDay.id}', ${completed})">
        <span class="material-icons" style="font-size:14px;">undo</span> Undo Last
      </button>` : ''}
      ${editLink}
    </div>`;
}

// ─── Calendar grid (month-view) ────────────────────────────────────────────

function _esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function _isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Renders a TimeTree-style month grid: each day cell shows up to maxVisible
// colored job bars (job title + customer color) and a "+N more" overflow link.
// Reuses the same .month-* CSS classes as dispatch.js renderMonthView so the
// driver-side calendar matches dispatch's TimeTree-inspired pattern.
//
// opts:
//   getBarsForDate: (year, month, day, isoDate) => [{ title, color }, ...]
//                   — caller resolves jobDay → bar info (title + customer color).
//                   Bars beyond maxVisible roll into "+N more".
//   onCellClick:    'window.JobBoardModule.jumpToDayFromCalendar' | similar
//   wrapperClass:   extra class on the .month-view container (e.g. 'jobboard-month-view')
//   maxVisible:     bars shown per cell before "+N more" (default 3, matches dispatch)
//   cellCountBadge: OPT-IN (TASK-563, jobboard driver view only). When true, a
//                   small count chip ("Nx") is rendered top-right on any day that
//                   has ≥1 bar, so a driver scanning a phone-sized month grid can
//                   see job density per day at a glance without the rich text the
//                   cell can't fit. Backward-compatible: dispatch.js does NOT pass
//                   this opt, so its month view is byte-identical (chip never
//                   renders there). TASK-9374: the chip is now a class-only
//                   <span class="month-day-count"> placed AFTER the day number
//                   (styled absolutely by .jobboard-month-view CSS) — the old
//                   inline display:flex on .month-day-num fought the today-circle
//                   rule and overlapped the numeral.
//   fillAdjacentDays: OPT-IN (TASK-9374, jobboard driver view). When true the
//                   grid ALWAYS has 42 cells (6 rows): the leading slots hold the
//                   previous month's trailing days and the trailing slots the next
//                   month's leading days, as <div class="month-day adjacent"
//                   data-date="iso"> with the numeral only — NO bars (the caller's
//                   scoped index is this-month-only) and the same click handler.
//                   Default false → the legacy ragged grid (leading `.empty`
//                   cells, no trailing cells) for dispatch.js, byte-identical.
//   fullTitle:      OPT-IN (TASK-9374). Render the whole (escaped) title and let
//                   CSS ellipsize; default false keeps the 12-char cut.
//   reserveMoreSlot: OPT-IN (TASK-9374). When the day has MORE bars than
//                   maxVisible, show maxVisible-1 bars and let "+N more" take the
//                   last slot, with N = bars actually hidden — so the cue never
//                   pushes the cell taller and never lies about what's visible.
//                   Default false = legacy (maxVisible bars + "+N more" below).
export function buildCalendarMonthGridHTML(anchorDate, opts = {}) {
  const a = new Date(anchorDate);
  a.setHours(0, 0, 0, 0);
  a.setDate(1);
  const year = a.getFullYear();
  const month = a.getMonth();

  const getBarsForDate = opts.getBarsForDate || (() => []);
  const onCellClick = opts.onCellClick || 'window.JobBoardModule.jumpToDayFromCalendar';
  const wrapperClass = opts.wrapperClass || '';
  const maxVisible = typeof opts.maxVisible === 'number' ? opts.maxVisible : 3;
  const cellCountBadge = !!opts.cellCountBadge;
  const fillAdjacentDays = !!opts.fillAdjacentDays;
  const fullTitle = !!opts.fullTitle;
  const reserveMoreSlot = !!opts.reserveMoreSlot;

  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayIso = _isoDate(today);

  const headerHtml = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
    .map(d => `<div class="month-weekday">${d}</div>`)
    .join('');

  // Adjacent-month cell: numeral only, no bars, same tap handler (the day sheet
  // / jump handles an empty or out-of-month day). `offset` is days relative to
  // the 1st of the rendered month (negative = previous month, > daysInMonth = next).
  const adjacentCell = (offset) => {
    const d = new Date(year, month, offset);
    const iso = _isoDate(d);
    return `
      <div class="month-day adjacent" data-date="${iso}" onclick="${onCellClick}('${iso}')">
        <div class="month-day-num">${d.getDate()}</div>
        <div class="month-day-jobs"></div>
      </div>`;
  };

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) {
    cells.push(fillAdjacentDays
      ? adjacentCell(1 - (firstWeekday - i))
      : '<div class="month-day empty"></div>');
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const cellDate = new Date(year, month, day);
    const iso = _isoDate(cellDate);
    const isToday = iso === todayIso;
    const bars = getBarsForDate(year, month, day, iso) || [];
    // reserveMoreSlot: the "+N more" line takes the LAST slot so it is never
    // clipped and N counts exactly the bars not painted.
    const showCount = (reserveMoreSlot && bars.length > maxVisible)
      ? Math.max(0, maxVisible - 1)
      : maxVisible;
    const visible = bars.slice(0, showCount);
    const moreCount = Math.max(0, bars.length - visible.length);
    const cellClasses = ['month-day'];
    if (isToday) cellClasses.push('today');
    // Click handler unconditional (matches dispatch.js renderMonthView):
    // empty days are still tappable and switch to day view, which renders
    // a graceful "no jobs" state.
    const handler = `onclick="${onCellClick}('${iso}')"`;

    const indicators = visible.map(b => {
      const safeTitle = _esc(b.title || 'Untitled');
      // fullTitle: whole title, still escaped — CSS (nowrap + ellipsis) does the cut.
      const shortTitle = fullTitle ? safeTitle : _esc((b.title || 'Untitled').substring(0, 12));
      const color = b.color || '#3B82F6';
      // TASK-320 Slice 2: `outlined: true` bars render as border-only chip
      // (dashed) instead of solid-fill — used for planned absences (OFF) so
      // they read as "scheduled" rather than alarm-coloured. Backward
      // compatible: callers that don't set `outlined` are unaffected.
      if (b.outlined) {
        const style = `background:transparent;border:1px dashed ${color};color:${color};`;
        return `<div class="month-job-indicator outlined" style="${style}" title="${safeTitle}">${shortTitle}</div>`;
      }
      return `<div class="month-job-indicator" style="background:${color};" title="${safeTitle}">${shortTitle}</div>`;
    }).join('');

    const moreLink = moreCount > 0
      ? `<span class="month-more-link">+${moreCount} more</span>`
      : '';

    // TASK-563: opt-in per-day count chip (jobboard driver view only). Renders a
    // tiny "Nx" badge next to the day number when the day has jobs, so phone-grid
    // density is legible at a glance. Inline-styled; dispatch never opts in.
    // TASK-9374: class-only chip AFTER the numeral (absolutely positioned by the
    // .jobboard-month-view CSS). No inline display:flex on .month-day-num, so
    // the today-circle rule owns that element outright.
    const countBadge = (cellCountBadge && bars.length > 0)
      ? `<span class="month-day-count">${bars.length}</span>`
      : '';
    const dayNumRow = `<div class="month-day-num">${day}</div>${countBadge}`;

    cells.push(`
      <div class="${cellClasses.join(' ')}" data-date="${iso}" ${handler}>
        ${dayNumRow}
        <div class="month-day-jobs">${indicators}${moreLink}</div>
      </div>`);
  }
  if (fillAdjacentDays) {
    // Always 6 rows (42 cells) so the viewport-locked row height is stable
    // month to month; trailing slots are the next month's leading days.
    let offset = daysInMonth + 1;
    while (cells.length < 42) cells.push(adjacentCell(offset++));
  }

  return `
    <div class="month-view ${wrapperClass}">
      <div class="month-header">${headerHtml}</div>
      <div class="month-grid">${cells.join('')}</div>
    </div>
  `;
}
